// React
import { useEffect, useState } from "react"

// Third-Party Libraries
import moment from "moment"
import toast from "react-hot-toast"

// Types
import type { SuccessFetch } from "types"
import type { DataType } from "../../types"

// Utils
import { useApi } from "utils"
import { group_list } from "pages/Finance/Transaction/utils"
import { useStoreFillter } from "./useStoreFillter"

// Types
interface FilterType {
  coa: string[]
  start_date: string
  end_date: string
  location_id: string
}
type FilterField = "end_date" | "location_id" | "start_date"

export function useList(): {
  data: DataType[]
  fetchData: () => void
  filter: FilterType
  isLoading: boolean
  onAddCoa: (value: string) => void
  onChangeFilter: (
    field: FilterField,
    value: string
  ) => void
  onRemoveCoa: (value: string) => void
} {
  // Hooks
  const api = useApi()
  const [data, setData] = useState<DataType[]>([])

  const { filter, setFilter } = useStoreFillter();

  const [isLoading, setLoading] = useState<boolean>(false)

  // Functions
  const fetchData = (): void => {
    setLoading(true)

    api.post(
      "/trxdetail/trxdetail",
      {
        coa_ids: filter.coa,
        date_start: filter.start_date,
        date_end: filter.end_date,
        location_id: filter.location_id
      }
    ).then((res: SuccessFetch<{
      payload: {
        amount: number
        coa_id: number
        coa_code: string
        coa_name: string
        contact: string
        description: string
        group_card: number
        position: 1 | 2
        referensi: string
        source: string
        transaction_date: string
      }[]
    }>) => {
      setData(res.data.payload.filter(item => {
        return filter.coa.includes(item.coa_id.toString())
      }).map(item => {
        // Vars
        let credit: number = 0
        let debit: number = 0

        if (item.position === 1) {
          debit = item.amount
        } else {
          credit = item.amount
        }

        return {
          credit,
          debit,
          coa_code: item.coa_code,
          coa_id: item.coa_id.toString(),
          coa_name: item.coa_name,
          contact: item.contact,
          description: item.description,
          group: group_list.find(i => i.value === item.group_card.toString())?.label ?? "-",
          referensi: item.referensi,
          source: item.source,
          transaction_date: moment(item.transaction_date).format("DD/MM/YYYY"),
        }
      }))
    }).catch(() => {
      setData([])
    }).finally(() => {
      setLoading(false)
    })
  }
  const onAddCoa = (value: string): void => {
    setFilter((prev: FilterType) => {
      // Vars
      const coa = [...prev.coa]
      const selectedCoa = coa.find(item => item === value)

      if (selectedCoa) {
        toast.error("COA is already selected")

        return prev
      }

      coa.push(value)

      return {
        ...prev,
        coa
      }
    })
  }
  const onChangeFilter = (
    field: FilterField,
    value: string
  ): void => {
    setFilter((prev: FilterType) => ({
      ...prev,
      [field]: value
    }))
  }
  const onRemoveCoa = (value: string): void => {
    setFilter((prev: FilterType) => {
      // Vars
      const coa = [...prev.coa]
      const index: number = coa.findIndex(item => item === value)

      coa.splice(index, 1)

      return {
        ...prev,
        coa
      }
    })
  }

  useEffect(() => {
    fetchData()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter])

  return { data, filter, isLoading, fetchData, onAddCoa, onChangeFilter, onRemoveCoa }
}